import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

interface CrossPlatformTrendChartProps {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function CrossPlatformTrendChart({ isEditMode, onRemove, onResize, onResetSize }: CrossPlatformTrendChartProps) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey, sourceType } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["unified-daily-trend", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("daily-trend", params));
      if (!res.ok) throw new Error("Failed to fetch trend data");
      return res.json() as Promise<Array<{
        date: string;
        spend: number;
        impressions: number;
        clicks: number;
        purchases: number;
        purchaseValue: number;
        roas: number;
      }>>;
    },
    enabled: sourceType === "unified",
  });

  const chartData = data?.map(d => ({
    date: d.date.slice(5),
    spend: d.spend,
    revenue: d.purchaseValue,
    roas: d.roas,
  })) || [];

  return (
    <DashboardWidget
      title="Cross-Platform Spend & Revenue"
      widgetType="chart"
      isEditMode={isEditMode}
      isLoading={isLoading}
      error={error?.message ?? null}
      onRetry={() => refetch()}
      onRemove={onRemove}
      onResize={onResize}
      onResetSize={onResetSize}
      platformSlug="unified"
    >
      <div className="p-4 h-full flex flex-col" data-testid="chart-cross-platform-trend">
        {chartData.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "#8C8578" }}>
            No trend data available
          </div>
        ) : (
          <div className="flex-1 min-h-0" style={{ background: "#F8F8F7" }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} tick={{ fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}x`} tick={{ fontSize: 10 }} />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === "roas") return [`${value.toFixed(2)}x`, "ROAS"];
                    return [`$${value.toLocaleString()}`, name === "spend" ? "Spend" : "Revenue"];
                  }}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Legend />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="spend"
                  stroke="#6366F1"
                  fill="#E0E7FF"
                  fillOpacity={0.3}
                  strokeWidth={2}
                  name="Spend"
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10B981"
                  fill="#D1FAE5"
                  fillOpacity={0.3}
                  strokeWidth={2}
                  name="Revenue"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="roas"
                  stroke="#F59E0B"
                  fill="none"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="ROAS"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </DashboardWidget>
  );
}
